import React, { useEffect, useState, useRef } from 'react';
import {
  DialogFullScreen as DFS,
  Image,
  ButtonPrimary,
  Type,
} from '@spotify-internal/encore-creator-web';
import { Link } from 'react-router-dom';
import { formatDisplayTimestamp, truncateStringAtLength } from '../../../lib/Formatter';
import { updateTrailer } from '../../../lib/Api/trailerSelection';
import styled from 'styled-components';
import {
  black,
  white,
  gray7,
  gray15,
  gray70,
  spacer4,
  spacer8,
  spacer12,
  spacer16,
  spacer64,
  azure,
  screenMdMax,
} from '@spotify-internal/encore-foundation';
import { getGradient } from '../../../vendor/Utils';
import { Trailer, Show } from '../../../types';
import { EpisodeTabs } from './EpisodeTabs';

type Props = {
  toggleTrailerSelectionModal: () => void;
  updateSelectedTrailer: (trailer: Trailer) => void;
  trailer?: Trailer;
  metadata: Show;
  podcastId: string;
};

enum RightPaneMode {
  SELECTION = 'selection',
  SUCCESS = 'success',
}

export function TrailerSelectionModal(props: Props) {
  const { podcastId, metadata } = props;

  const [selectedEpisode, setSelectedEpisode] = useState<Trailer>();
  const [primaryButtonDisabled, setPrimaryButtonDisabled] = useState<boolean>(true);
  const [backgroundGradient, setBackgroundGradient] = useState<string>();
  const [rightPaneMode, setRightPaneMode] = useState<string>(RightPaneMode.SELECTION);

  const rightPaneEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async function() {
      const { backgroundGradient } = await getGradient(metadata.artworkUrl);
      /* istanbul ignore next */
      setBackgroundGradient(backgroundGradient);
    })();
  }, []);

  useEffect(() => {
    const { trailer } = props;

    if (trailer) {
      setSelectedEpisode(trailer);
    }
  }, [props.trailer]);

  const handleEpisodeClick = episode => {
    setSelectedEpisode(episode);
    setPrimaryButtonDisabled(false);
  };

  const handleSetTrailer = async () => {
    try {
      const { trailer } = props;
      setPrimaryButtonDisabled(true);
      await updateTrailer(podcastId, selectedEpisode!.id, trailer ? trailer.id : undefined);
      setRightPaneMode(RightPaneMode.SUCCESS);
    } catch {
      setPrimaryButtonDisabled(false);
    }
  };

  const calculatePodcastNameFontSize = length => {
    // decrease the font size of longer titles, bound by a min of 16 and a max of 36
    /* istanbul ignore else */
    if (length > 10) {
      let adjustedFontSize = Math.floor((length - 100) / -2.5);
      return Math.max(16, adjustedFontSize);
    } else {
      return 36;
    }
  };

  const renderEpisodePreviewPane = () => {
    return (
      <PreviewContainer>
        <PreviewHeader backgroundGradient={backgroundGradient}>
          <Image
            src={metadata.artworkUrl}
            alt="Podcast Artwork"
            imageHeight="104px"
            imageWidth="104px"
            crop
            style={{
              marginLeft: spacer16,
              boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.25)',
              borderRadius: '8px',
              flex: '0 0 104px',
            }}
          />
          <PreviewHeaderTitle>
            <PreviewPodcastName fontSize={calculatePodcastNameFontSize(metadata.name.length)}>
              {metadata.name}
            </PreviewPodcastName>
            <Type as="p" variant={Type.body2} color={white} condensed>
              {metadata.publisher}
            </Type>
          </PreviewHeaderTitle>
        </PreviewHeader>

        <Type
          as="p"
          variant={Type.body3}
          color={gray70}
          style={{ margin: spacer16 }}
          dangerouslySetInnerHTML={{ __html: truncateStringAtLength(metadata.description, 150) }}
        />

        {selectedEpisode ? (
          <TrailerPreview>
            <Image
              src={selectedEpisode.artworkUrl}
              alt=""
              imageHeight="64px"
              imageWidth="64px"
              crop
              style={{
                boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.25)',
                borderRadius: '8px',
              }}
            />
            <TrailerPreviewTitle>
              <Type
                as="p"
                variant={Type.body2}
                weight={Type.bold}
                color={white}
                condensed
                style={{ paddingBottom: spacer4 }}
              >
                {selectedEpisode.name}
              </Type>
              <div style={{ display: 'flex' }}>
                <Chip>Trailer</Chip>
                <Type as="p" variant={Type.body3} color={white} condensed>
                  {formatDisplayTimestamp(selectedEpisode.duration / 1000)}
                </Type>
              </div>
            </TrailerPreviewTitle>
          </TrailerPreview>
        ) : (
          <TrailerPlaceholder>
            <Type
              as="p"
              variant={Type.body2}
              color={gray70}
              condensed
              style={{ textAlign: 'center', width: '100%' }}
            >
              Podcast trailer
            </Type>
          </TrailerPlaceholder>
        )}
        <div style={{ margin: spacer16 }}>
          <Type as="h3" variant={Type.heading4} color={white}>
            All episodes
          </Type>
          <AllEpisodesPlaceholder />
        </div>
      </PreviewContainer>
    );
  };

  const dialogElements = () => {
    return {
      [RightPaneMode.SELECTION]: {
        body: (
          <ModalBody>
            {renderEpisodePreviewPane()}
            <RightPane ref={rightPaneEl}>
              <Type as="h3" variant={Type.heading3}>
                Pick an episode
              </Type>
              <Type as="p" variant={Type.body1}>
                Introduce your podcast to Spotify listeners with a short episode.
                <br /> Click on an episode to select it as your trailer.
              </Type>
              <Type as="p" variant={Type.body1}>
                To learn more about podcast trailers on Spotify,{' '}
                <Link
                  to="/faq#how-can-i-add-a-trailer-for-my-podcast"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: azure }}
                >
                  click here.
                </Link>
              </Type>
              <EpisodeTabs
                podcastId={podcastId}
                handleEpisodeClick={handleEpisodeClick}
                selectedEpisode={selectedEpisode}
                rightPaneEl={rightPaneEl}
              />
            </RightPane>
          </ModalBody>
        ),
        footer: (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonPrimary disabled={primaryButtonDisabled} onClick={handleSetTrailer}>
              Set Trailer
            </ButtonPrimary>
          </div>
        ),
      },
      [RightPaneMode.SUCCESS]: {
        body: (
          <ModalBody>
            {renderEpisodePreviewPane()}
            <RightPane>
              <Type as="h3" variant={Type.heading3}>
                Your trailer has been set!
              </Type>
              <Type as="p" variant={Type.body1}>
                Listeners on mobile will see your podcast trailer in Spotify within an hour.
                <br /> Spotify podcast trailers are currently not available for listeners on the
                desktop and web versions.
              </Type>
            </RightPane>
          </ModalBody>
        ),
        footer: (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonPrimary
              onClick={() => {
                props.toggleTrailerSelectionModal();
                props.updateSelectedTrailer(selectedEpisode!);
              }}
            >
              Got It
            </ButtonPrimary>
          </div>
        ),
      },
    }[rightPaneMode];
  };

  const { body, footer } = dialogElements();

  return (
    <DialogFullScreen
      onClose={() => {
        props.toggleTrailerSelectionModal();

        /* istanbul ignore next */
        if (RightPaneMode.SUCCESS) {
          props.updateSelectedTrailer(selectedEpisode!);
        }
      }}
      dialogTitle={props.trailer ? 'Manage your trailer' : 'Select your trailer'}
      body={body}
      bodyStyle={{ padding: '0', overflow: 'hidden' }}
      footer={footer}
    />
  );
}

const DialogFullScreen = styled(DFS)`
  padding: 0 !important;
`;

const ModalBody = styled.div`
  display: flex;
  height: 100%;
`;

const PreviewContainer = styled.div`
  background: ${gray7};
  flex: 0 0 400px;

  @media (max-width: ${screenMdMax}) {
    display: none;
  }
`;

const PreviewHeader = styled.div<{ backgroundGradient?: string }>`
  background: ${props => props.backgroundGradient};
  display: flex;
  align-items: center;
  padding-top: 80px;
`;

const PreviewHeaderTitle = styled.div`
  margin: 0 12px;
`;

const PreviewPodcastName = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading2,
  color: white,
  condensed: true,
})<{ fontSize: number }>`
  line-height: 1.2;
  font-size: ${props => `${props.fontSize}px`};
`;

const TrailerPlaceholder = styled.div`
  display: flex;
  align-items: center;
  height: 88px;
  border: 1px dashed #9e9e9e;
  border-radius: 8px;
  margin: ${spacer16};
`;

const TrailerPreview = styled.div`
  display: flex;
  align-items: center;
  border-radius: 8px;
  margin: ${spacer16};
  padding: ${spacer12};
  background-color: ${gray15};
`;

const TrailerPreviewTitle = styled.div`
  margin-left: ${spacer12};
`;

const Chip = styled(Type).attrs({
  forwardedAs: 'p',
  variant: Type.cta4,
  color: black,
  condensed: true,
})`
  background-color: ${gray70};
  border-radius: 2px;
  padding: 0 ${spacer4};
  margin-right: ${spacer8};
`;

const AllEpisodesPlaceholder = styled.div`
  background: linear-gradient(${gray15}, ${gray7});
  height: 200px;
  width: 100%;
  border-radius: 8px;
`;

const RightPane = styled.div`
  overflow-y: scroll;
  flex: 1 1 auto;
  padding: ${spacer64};
`;
